import type {
	ChipColor,
	ChipComponent,
} from "@app/widgets/base/chip/chip.component";
import { DatepickerDotColor } from "@app/widgets/base/datepicker/datepicker.component";
import type { CbSelectOption } from "@app/widgets/base/select/select.component";

export const DEADLINE_TEMPLATE_TYPE_OPTIONS: CbSelectOption[] = [
	{
		label: "DEADLINES.DEADLINE_TEMPLATE.TYPE.COMPANY",
		value: "company",
	},
	{
		label: "DEADLINES.DEADLINE_TEMPLATE.TYPE.STAFF",
		value: "staff_member",
	},
	{
		label: "DEADLINES.DEADLINE_TEMPLATE.TYPE.VEHICLES",
		value: "vehicle",
	},
	{
		label: "DEADLINES.DEADLINE_TEMPLATE.TYPE.INTERNAL",
		value: "internal",
	},
];

export const DEADLINE_TEMPLATE_TYPE_LABEL_MAP = {
	company: "DEADLINES.DEADLINE_TEMPLATE.TYPE.COMPANY",
	staff_member: "DEADLINES.DEADLINE_TEMPLATE.TYPE.STAFF",
	vehicle: "DEADLINES.DEADLINE_TEMPLATE.TYPE.VEHICLES",
	internal: "DEADLINES.DEADLINE_TEMPLATE.TYPE.INTERNAL",
};

export const EVENT_TYPE_COLOR_MAP: Record<
	string,
	Exclude<ReturnType<typeof ChipComponent.prototype.color>, "mapped" | "auto">
> = {
	illness: "red",
	permission: "green",
	holiday: "yellow",
	restriction: "orange",
	appointment: "blue",
	other: "gray",
};

export const EVENT_TYPE_ICON_MAP: Record<string, string> = {
	illness: "cb-solid-rounded-health",
	permission: "cb-solid-rounded-clock-05",
	holiday: "cb-solid-rounded-sun-02",
	restriction: "cb-solid-rounded-unavailable",
	appointment: "cb-solid-rounded-calendar-02",
	other: "cb-solid-rounded-help-circle",
};

export const DEADLINE_SUBJECT_TYPE_COLOR_MAP: Record<
	string,
	DatepickerDotColor
> = {
	company: DatepickerDotColor.violet,
	staff_member: DatepickerDotColor.yellow,
	vehicle: DatepickerDotColor.blue,
	internal: DatepickerDotColor.orange,
	document: DatepickerDotColor.red,
};

export const DEADLINE_SUBJECT_TYPE_ICON_MAP: Record<string, string> = {
	company: "cb-solid-rounded-building-03",
	staff_member: "cb-app-bold-user-group",
	vehicle: "cb-solid-rounded-truck-delivery",
	internal: "cb-solid-rounded-settings-02",
	document: "cb-solid-rounded-file-02",
};


const CATEGORY_OPTION: Record<string, CbSelectOption> = {
	durc: { label: "DEADLINES.DEADLINE_TEMPLATE.CATEGORY.DURC", value: "durc" },
	license: {
		label: "DEADLINES.DEADLINE_TEMPLATE.CATEGORY.LICENSE",
		value: "license",
	},
	insurance: {
		label: "DEADLINES.DEADLINE_TEMPLATE.CATEGORY.INSURANCE",
		value: "insurance",
	},
	certificate: {
		label: "DEADLINES.DEADLINE_TEMPLATE.CATEGORY.CERTIFICATE",
		value: "certificate",
	},
	courses: {
		label: "DEADLINES.DEADLINE_TEMPLATE.CATEGORY.COURSES",
		value: "courses",
	},
	medical_visit: {
		label: "DEADLINES.DEADLINE_TEMPLATE.CATEGORY.MEDICAL_VISIT",
		value: "medical_visit",
	},
	document: {
		label: "DEADLINES.DEADLINE_TEMPLATE.CATEGORY.DOCUMENT",
		value: "document",
	},
	other: { label: "DEADLINES.DEADLINE_TEMPLATE.CATEGORY.OTHER", value: "other" },
};

export const DEADLINE_TEMPLATE_CATEGORY_OPTIONS: CbSelectOption[] =
	Object.values(CATEGORY_OPTION);

/**
 * Categorie di scadenza pertinenti per ogni tipo entità: il cliente non vuole
 * durc/assicurazione/licenza sul personale, ma la visita medica; i veicoli
 * hanno licenza/assicurazione, ecc. `internal` mostra tutto.
 */
export const DEADLINE_TEMPLATE_CATEGORY_OPTIONS_BY_TYPE: Record<
	string,
	CbSelectOption[]
> = {
	company: [
		CATEGORY_OPTION.durc,
		CATEGORY_OPTION.insurance,
		CATEGORY_OPTION.certificate,
		CATEGORY_OPTION.document,
		CATEGORY_OPTION.other,
	],
	staff_member: [
		CATEGORY_OPTION.medical_visit,
		CATEGORY_OPTION.certificate,
		CATEGORY_OPTION.courses,
		CATEGORY_OPTION.document,
		CATEGORY_OPTION.other,
	],
	vehicle: [
		CATEGORY_OPTION.license,
		CATEGORY_OPTION.insurance,
		CATEGORY_OPTION.certificate,
		CATEGORY_OPTION.document,
		CATEGORY_OPTION.other,
	],
	internal: DEADLINE_TEMPLATE_CATEGORY_OPTIONS,
};

/**
 * Ritorna le categorie ammesse per un tipo entità; tipo ignoto/nullo -> tutte.
 */
export function deadlineCategoryOptionsForType(
	type: string | null | undefined,
): CbSelectOption[] {
	return type
		? (DEADLINE_TEMPLATE_CATEGORY_OPTIONS_BY_TYPE[type] ??
				DEADLINE_TEMPLATE_CATEGORY_OPTIONS)
		: DEADLINE_TEMPLATE_CATEGORY_OPTIONS;
}

export const DEADLINE_TEMPLATE_CATEGORY_LABEL_MAP: Record<string, string> = {
	durc: "DEADLINES.DEADLINE_TEMPLATE.CATEGORY.DURC",
	license: "DEADLINES.DEADLINE_TEMPLATE.CATEGORY.LICENSE",
	insurance: "DEADLINES.DEADLINE_TEMPLATE.CATEGORY.INSURANCE",
	certificate: "DEADLINES.DEADLINE_TEMPLATE.CATEGORY.CERTIFICATE",
	courses: "DEADLINES.DEADLINE_TEMPLATE.CATEGORY.COURSES",
	medical_visit: "DEADLINES.DEADLINE_TEMPLATE.CATEGORY.MEDICAL_VISIT",
	document: "DEADLINES.DEADLINE_TEMPLATE.CATEGORY.DOCUMENT",
	other: "DEADLINES.DEADLINE_TEMPLATE.CATEGORY.OTHER",
};

/** Colori fissi e distinti per categoria (chip color="mapped"). */
export const DEADLINE_TEMPLATE_CATEGORY_COLOR_MAP: Record<string, ChipColor> = {
	durc: "error",
	license: "blue",
	insurance: "info",
	certificate: "green",
	courses: "orange",
	medical_visit: "pink",
	document: "warning",
	other: "gray",
};

/** Lettera del badge per il tipo evento staff sul calendario (F=ferie, P=permesso). */
export const EVENT_TYPE_BADGE_LETTER_MAP: Record<string, string> = {
	holiday: "F",
	permission: "P",
	illness: "M",
	restriction: "L",
	appointment: "A",
	other: "E",
};